import { Activity, BarChart3, Boxes, ChartPie, Database, FileCog, FlaskConical, LogOut, Map as MapIcon, Menu, MessageSquare, Package, PanelLeft, Receipt, Settings as SettingsIcon, SlidersHorizontal, Tag, Users } from "lucide-react";
import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { AuthModeBadge } from "@/components/AuthModeIndicator";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  permission?: string;
  /** Активен только при точном совпадении пути: `/reviews` не должен гореть на `/reviews/dashboard`. */
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/reviews", label: "Отзывы", icon: MessageSquare, permission: "reviews", end: true },
  { to: "/reviews/dashboard", label: "Дашборд отзывов", icon: Activity, permission: "reviews" },
  { to: "/supply", label: "Поставки", icon: Boxes, permission: "supply" },
  { to: "/orders", label: "Заказы", icon: BarChart3, permission: "orders" },
  { to: "/ozon-analytics", label: "Аналитика МП", icon: ChartPie, permission: "ozon_analytics" },
  { to: "/wholesale", label: "Аналитика ОПТ", icon: Package, permission: "wholesale_analytics" },
  { to: "/wb-regions", label: "WB: регионы", icon: MapIcon, permission: "wb_regions" },
  { to: "/purchase-prices", label: "Себестоимость", icon: Receipt, permission: "purchase_prices" },
  { to: "/product-dev", label: "Разработка продукции", icon: FlaskConical, permission: "product_dev" },
  { to: "/settings/1c", label: "Подключение 1С", icon: SettingsIcon },
  { to: "/admin/prompts", label: "Промпты", icon: SlidersHorizontal, adminOnly: true },
  { to: "/admin/brands", label: "Бренды", icon: Tag, adminOnly: true },
  { to: "/admin/onec", label: "Настройки 1С", icon: FileCog, adminOnly: true },
  { to: "/admin/onec-bases", label: "Базы 1С (SSO)", icon: Database, adminOnly: true },
  { to: "/admin/users", label: "Пользователи", icon: Users, adminOnly: true },
];

export function Layout(): React.ReactElement {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Свёрнутость бокового меню на десктопе (даёт странице всю ширину); помним между сессиями.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("navCollapsed") === "1",
  );
  const toggleCollapsed = (): void =>
    setCollapsed((s) => {
      localStorage.setItem("navCollapsed", s ? "0" : "1");
      return !s;
    });

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const visible = NAV_ITEMS.filter((item) => {
    if (!user) return false;
    if (item.adminOnly && !user.is_admin) return false;
    if (item.permission && !user.is_admin && !user.permissions.includes(item.permission))
      return false;
    return true;
  });

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setDrawerOpen((s) => !s)}
            aria-label="Меню"
          >
            <Menu className="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
          >
            <PanelLeft className="size-5" />
          </Button>
          <span className="text-lg font-semibold">Продавец</span>
        </div>
        <div className="flex min-w-0 items-center gap-2 text-sm">
          {/* Логин виден ВСЕГДА, и на телефоне тоже (как в wh-helper) — иначе
              непонятно, под кем зашли; обрезаем троеточием, чтобы не ломать
              мобильную ширину шапки. */}
          <span className="min-w-0 max-w-[40vw] truncate text-muted-foreground">
            {user?.username}
          </span>
          <AuthModeBadge mode={user?.auth_mode} />
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Выйти">
            <LogOut className="size-4" />
          </Button>
        </div>
      </header>

      <div className="flex flex-1">
        <aside
          className={cn(
            // телефон: от шапки до низа экрана (dvh — без панели Safari) со своей прокруткой, иначе пункты ниже
            // экрана обрезались (22.10.2026); десктоп — в потоке, overflow-hidden нужен для сворачивания в ноль
            "fixed top-14 bottom-0 left-0 z-20 w-64 shrink-0 overflow-y-auto overscroll-contain border-r bg-card pb-[env(safe-area-inset-bottom)] transition-all md:static md:translate-x-0 md:overflow-hidden",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
            collapsed && "md:w-0 md:border-r-0", // на десктопе сворачиваем в ноль — страница на всю ширину
          )}
        >
          <nav className="flex flex-col gap-1 p-3">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? item.to === "/"}
                onClick={() => setDrawerOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-3 text-sm transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )
                }
              >
                <item.icon className="size-4 shrink-0" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {drawerOpen && (
          <button
            type="button"
            className="fixed inset-0 z-10 bg-black/30 md:hidden dark:bg-black/60"
            onClick={() => setDrawerOpen(false)}
            aria-label="Закрыть меню"
          />
        )}

        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
